import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import { post } from "../../../../utils/api";
import BulkFileUpload from "../../../Scope1/FugitiveEmmissions/BulkFileUpload";

const TITLE_MONTH = "Month (in 01/25 or Jan-25)";
const TITLE_TRANSPORT_TYPE = "Transportation Type";
const TITLE_ORIGIN = "Origin";
const TITLE_DESTINATION = "Destination";
const TITLE_FREIGHT = "Freight Load (in Tonnes)";

const getEnteredMonth = (row) =>
  row?.consumptionPeriod ||
  row?.consumptionMonth ||
  row?.[TITLE_MONTH] ||
  row?.["Consumption Month"] ||
  "";

const getEnteredTransportType = (row) =>
  row?.transportationType ||
  row?.[TITLE_TRANSPORT_TYPE] ||
  row?.["Transportation type"] ||
  "";

const getEnteredOrigin = (row) =>
  row?.origin || row?.[TITLE_ORIGIN] || row?.Source || "";

const getEnteredDestination = (row) =>
  row?.destination || row?.[TITLE_DESTINATION] || "";

const getEnteredFreightLoad = (row) =>
  row?.freightLoad != null && row?.freightLoad !== ""
    ? String(row.freightLoad)
    : row?.[TITLE_FREIGHT] != null && row?.[TITLE_FREIGHT] !== ""
      ? String(row[TITLE_FREIGHT])
      : "";

const BULK_UPLOAD_JSON_SNACK_DURATION = 4000;

const RailTransportationBulkUploadReview = ({
  matchData = [],
  bulkUploadTable,
  onClose,
  onConfirm,
  formDataBulkUpload = [],
  setFormDataBulkUpload,
  setsnackOpen,
  bulkUploadUrl = "/scope3/railTransportation/jsonupload",
}) => {
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] =
    useState(false);
  const [uploading, setUploading] = useState(false);
  const [skippedPreview, setSkippedPreview] = useState([]);

  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none");
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);

  const formDataArray = useMemo(() => {
    if (Array.isArray(formDataBulkUpload)) return formDataBulkUpload;
    return Object.values(formDataBulkUpload || {});
  }, [formDataBulkUpload]);

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    const filesArray = Array.isArray(files) ? files : Object.values(files || {});
    setCommonDocument(filesArray);
    if (formDataArray.length > 0) {
      setFormDataBulkUpload(
        formDataArray.map((entry) => ({
          ...entry,
          "Document (optional)": filesArray,
        }))
      );
    }
    if (filesArray.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleClearCommonDocument = () => {
    setCommonDocument(null);
    if (formDataArray.length > 0) {
      setFormDataBulkUpload(
        formDataArray.map((entry) => ({
          ...entry,
          "Document (optional)": null,
        }))
      );
    }
  };

  const handleIndividualDocumentChange = (index, files) => {
    const filesArray = Array.isArray(files) ? files : Object.values(files || {});
    setIndividualDocuments((prev) => ({ ...prev, [index]: filesArray }));
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : Object.values(prev || {});
      return arr.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": filesArray } : entry
      );
    });
    if (filesArray.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleIndividualDocumentClear = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : Object.values(prev || {});
      return arr.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": null } : entry
      );
    });
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push(
        "Please select a document upload type when document upload is confirmed."
      );
    }
    return validationErrors;
  };

  const performUpload = async () => {
    try {
      setUploading(true);
      const dataToUseArray = Array.isArray(formDataBulkUpload)
        ? formDataBulkUpload
        : Object.values(formDataBulkUpload || {});

      const res = await post(bulkUploadUrl, {
        excelData: dataToUseArray,
        documentUploadType,
        commonDocument,
        individualDocuments,
      });

      const inserted = Number(res?.data?.inserted ?? 0);
      const skipped = Number(res?.data?.skipped ?? 0);

      if (inserted === 0) {
        setSkippedPreview(res?.data?.skippedEntries || []);
        setsnackOpen?.({
          open: true,
          message:
            skipped > 0
              ? `No data inserted. ${skipped} row${skipped > 1 ? "s were" : " was"} skipped. Please review and try again.`
              : res?.data?.message ||
                "No data inserted. Please review and try again.",
          severity: "error",
          autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
        });
        return;
      }

      const responseData = res?.data ?? {};
      const skippedCount = Number(responseData?.skipped ?? skipped ?? 0);
      const apiMessage = responseData?.message;
      const successMessage = "Data uploaded successfully";
      setsnackOpen?.({
        open: true,
        message:
          skippedCount > 0
            ? apiMessage ||
              `${inserted} entr${inserted === 1 ? "y" : "ies"} inserted. ${skippedCount} row${skippedCount === 1 ? "" : "s"} skipped.`
            : successMessage,
        severity: skippedCount > 0 ? "warning" : "success",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });

      onConfirm?.(responseData);
      onClose?.();
    } catch (err) {
      const errMessage = err.response?.data?.details
        ?.map((el) => el?.error || el?.reason || el?.msg)
        ?.filter(Boolean)
        ?.join(", ");
      setsnackOpen?.({
        open: true,
        message:
          errMessage ?? err.response?.data?.message ?? "Network Error",
        severity: "error",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }

    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }

    await performUpload();
  };

  if (!bulkUploadTable || !matchData?.length) return null;

  return (
    <>
      <Dialog
        open={bulkUploadTable}
        onClose={onClose}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            margin: "16px",
            maxHeight: "calc(100% - 32px)",
            zIndex: 1000,
          },
        }}
      >
        <DialogTitle>Review Rail Transportation Bulk Upload</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review your rail transportation bulk upload data before confirming.
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={5} sx={{ fontWeight: "bold", width: "55%" }}>
                    Data entered
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                    Master data
                  </TableCell>
                  {documentUploadConfirmed &&
                    documentUploadType === "individual" && (
                      <TableCell sx={{ fontWeight: "bold", width: "15%" }}>
                        Document
                      </TableCell>
                    )}
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Consumption month</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Transportation type</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Destination</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Freight load (Tonnes)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} />
                  {documentUploadConfirmed &&
                    documentUploadType === "individual" && (
                      <TableCell sx={{ fontWeight: "bold" }} />
                    )}
                </TableRow>
              </TableHead>
              <TableBody>
                {matchData.map((item, index) => {
                  const enteredMonth =
                    getEnteredMonth(item) || getEnteredMonth(formDataArray[index]);
                  const enteredType =
                    getEnteredTransportType(item) ||
                    getEnteredTransportType(formDataArray[index]);
                  const enteredOrigin =
                    getEnteredOrigin(item) || getEnteredOrigin(formDataArray[index]);
                  const enteredDestination =
                    getEnteredDestination(item) ||
                    getEnteredDestination(formDataArray[index]);
                  const enteredFreight =
                    getEnteredFreightLoad(item) ||
                    getEnteredFreightLoad(formDataArray[index]);
                  return (
                    <TableRow key={index}>
                      <TableCell>{enteredMonth || "—"}</TableCell>
                      <TableCell>{enteredType || "—"}</TableCell>
                      <TableCell>{enteredOrigin || "—"}</TableCell>
                      <TableCell>{enteredDestination || "—"}</TableCell>
                      <TableCell>{enteredFreight || "—"}</TableCell>
                      <TableCell>
                        <Chip label="Matched" color="success" size="small" />
                      </TableCell>
                      {documentUploadConfirmed &&
                        documentUploadType === "individual" && (
                          <TableCell>
                            <BulkFileUpload
                              filesList={individualDocuments[index] || []}
                              handleChange={handleIndividualDocumentChange}
                              handleClear={handleIndividualDocumentClear}
                              index={index}
                            />
                          </TableCell>
                        )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Document upload
            </Typography>
            <Box sx={{ width: "100%" }}>
              <FormControl
                component="fieldset"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                  m: 0,
                }}
              >
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, color: "text.primary", fontSize: "0.875rem" }}
                >
                  Do you want to upload document?
                </FormLabel>
                <RadioGroup
                  value={documentUploadConfirmed ? "yes" : "no"}
                  onChange={(e) =>
                    handleDocumentUploadConfirmation(e.target.value === "yes")
                  }
                  row
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Box>

            {documentUploadConfirmed && (
              <Box
                sx={{
                  width: "100%",
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <FormControl
                  component="fieldset"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    m: 0,
                  }}
                >
                  <FormLabel
                    component="legend"
                    sx={{ mb: 1, color: "text.primary", fontSize: "0.875rem" }}
                  >
                    Document upload type
                  </FormLabel>
                  <RadioGroup
                    value={documentUploadType}
                    onChange={handleDocumentUploadTypeChange}
                    row
                    sx={{ flexWrap: "wrap", gap: 0.5 }}
                  >
                    <FormControlLabel
                      value="same"
                      control={<Radio />}
                      label="Same file for all records"
                    />
                    <FormControlLabel
                      value="individual"
                      control={<Radio />}
                      label="Individual file for each record"
                    />
                  </RadioGroup>
                </FormControl>

                {documentUploadType === "same" && (
                  <Box sx={{ mt: 2, width: "100%" }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Upload a single document that will be associated with all records:
                    </Typography>
                    <BulkFileUpload
                      filesList={commonDocument}
                      handleChange={handleCommonDocumentChange}
                      handleClear={handleClearCommonDocument}
                    />
                  </Box>
                )}

                {documentUploadType === "individual" && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 2, color: "text.secondary" }}
                  >
                    Upload individual documents for each record. Documents will appear
                    as a new column in the table above.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#ffebee",
                borderRadius: 1,
                border: "1px solid #f44336",
              }}
            >
              <Typography
                variant="h6"
                color="error"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Error:
              </Typography>
              {uploadValidationErrors.map((error, idx) => (
                <Typography key={idx} variant="body2" color="error" sx={{ mb: 0.5 }}>
                  • {error}
                </Typography>
              ))}
            </Box>
          )}

          {skippedPreview.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#fff8e1",
                borderRadius: 1,
                border: "1px solid #ffb300",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Skipped rows
              </Typography>
              {skippedPreview.map((entry, idx) => (
                <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                  •{" "}
                  {typeof entry === "string"
                    ? entry
                    : entry?.error || entry?.message || JSON.stringify(entry)}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="primary"
            disabled={uploading}
          >
            Confirm upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showNoDocumentDialog}
        onClose={() => setShowNoDocumentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">No documents uploaded</Typography>
            <IconButton
              onClick={() => setShowNoDocumentDialog(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have selected to upload documents but haven&apos;t uploaded any files.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to proceed without uploading documents?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={() => setShowNoDocumentDialog(false)}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
          >
            Upload documents
          </Button>
          <Button
            onClick={() => {
              setShowNoDocumentDialog(false);
              performUpload();
            }}
            variant="contained"
            color="primary"
          >
            Proceed without documents
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RailTransportationBulkUploadReview;
